import { create } from 'zustand';
import { wsManager } from '../lib/websocket';
import {
  getAccessToken,
  setAccessToken,
  removeAccessToken,
  getRefreshToken,
  setRefreshToken,
  clearAuthStorage,
} from '../lib/auth-tokens';

// Re-export token helpers for backward compatibility (used by auth.store.spec.ts)
export { getAccessToken, setAccessToken, removeAccessToken, getRefreshToken, setRefreshToken };

const API_BASE_URL = import.meta.env.VITE_BFF_URL || 'http://localhost:3010';

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  permissions?: string[];
}

export interface Organization {
  id: string;
  name: string;
  slug?: string;
  plan?: string;
}

export interface OrgMembership {
  orgId: string;
  organization: Organization;
  role: 'owner' | 'admin' | 'member';
  isDefault: boolean;
}

export interface Subscription {
  plan: string;
  status: string;
  trialEndsAt?: string;
  currentPeriodEnd?: string;
}

interface AuthState {
  user: User | null;
  organization: Organization | null;
  subscription: Subscription | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  organizations: OrgMembership[];
  activeOrgId: string | null;
  defaultOrgId: string | null;

  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  fetchMe: () => Promise<void>;
  switchOrg: (orgId: string) => Promise<void>;
  fetchOrganizations: () => Promise<void>;
}

interface RegisterData {
  firstName?: string;
  lastName?: string;
  email: string;
  password: string;
  companyName?: string;
}

interface LoginApiResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    refreshToken?: string;
    user: { id: string; email: string };
    expires: string;
  } | null;
}

/**
 * Returns common headers for API requests.
 * Includes Bearer token when available (needed for fetchMe, logout).
 */
function getAuthHeaders(): HeadersInit {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Zustand store for authentication state management.
 * Follows Trinity AI pattern adapted for Zuroq's existing API endpoints.
 */
export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  organization: null,
  subscription: null,
  token: getAccessToken(),
  isAuthenticated: !!getAccessToken(),
  isLoading: false,
  organizations: [],
  activeOrgId: localStorage.getItem('activeOrgId'),
  defaultOrgId: localStorage.getItem('defaultOrgId'),

  login: async (email: string, password: string) => {
    set({ isLoading: true });
    try {
      const response = await fetch(`${API_BASE_URL}/auth/v3/login`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          (errorData as { message?: string }).message || `Login failed (${response.status})`,
        );
      }

      const data = await response.json();
      const payload = data.data;
      if (!payload?.token) {
        throw new Error(data.message || 'Login failed');
      }

      setAccessToken(payload.token);
      if (payload.refreshToken) setRefreshToken(payload.refreshToken);
      localStorage.setItem('user', JSON.stringify(payload.user));
      localStorage.setItem('tokenExpiry', payload.expires);

      const activeOrgId = payload.user?.orgId || null;
      if (activeOrgId) {
        localStorage.setItem('activeOrgId', activeOrgId);
      }

      set({
        user: payload.user,
        token: payload.token,
        isAuthenticated: true,
        isLoading: false,
        activeOrgId,
      });
      wsManager.connect();
      // Fetch orgs in background so OrgSwitcher renders immediately
      get().fetchOrganizations();
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  register: async (data: RegisterData) => {
    set({ isLoading: true });
    try {
      const response = await fetch(`${API_BASE_URL}/auth/v3/signup`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          ...(data.firstName && { firstName: data.firstName }),
          ...(data.lastName && { lastName: data.lastName }),
          ...(data.companyName && { companyName: data.companyName }),
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          (errorData as { message?: string }).message || `Registration failed (${response.status})`,
        );
      }

      const result: LoginApiResponse = await response.json();
      if (!result.success || !result.data) {
        throw new Error(result.message || 'Registration failed');
      }

      setAccessToken(result.data.token);
      if (result.data.refreshToken) setRefreshToken(result.data.refreshToken);
      localStorage.setItem('user', JSON.stringify(result.data.user));
      localStorage.setItem('tokenExpiry', result.data.expires);

      set({
        user: result.data.user,
        token: result.data.token,
        isAuthenticated: true,
        isLoading: false,
      });
      wsManager.connect();
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  logout: async () => {
    try {
      await fetch(`${API_BASE_URL}/auth/v3/logout`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
    } catch {
      // Logout API failure is non-critical — clear local state regardless
    }
    wsManager.disconnect();
    clearAuthStorage();
    set({
      user: null,
      organization: null,
      subscription: null,
      token: null,
      isAuthenticated: false,
      organizations: [],
      activeOrgId: null,
      defaultOrgId: null,
    });
  },

  fetchMe: async () => {
    set({ isLoading: true });
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error('No token');
      }

      // Check token expiry from localStorage as a quick pre-check
      const expiry = localStorage.getItem('tokenExpiry');
      if (expiry && new Date(expiry) < new Date()) {
        throw new Error('Token expired');
      }

      // Validate token server-side (catches revoked tokens, rotated secrets)
      let user: { id: string; email: string } | null = null;
      let activeOrgId: string | null = null;
      try {
        const response = await fetch(`${API_BASE_URL}/auth/v3/status`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Status check failed (${response.status})`);
        }

        const data: LoginApiResponse = await response.json();
        if (!data.success || !data.data) {
          throw new Error('Token invalid');
        }

        user = data.data.user;
        // Update stored token if server returned a fresh one
        if (data.data.token) {
          setAccessToken(data.data.token);
          localStorage.setItem('tokenExpiry', data.data.expires);
        }
        localStorage.setItem('user', JSON.stringify(user));
      } catch {
        // Network failure — fall back to localStorage (offline resilience)
        const storedUser = localStorage.getItem('user');
        user = storedUser ? JSON.parse(storedUser) : null;
        if (!user) {
          throw new Error('No user data');
        }
      }

      activeOrgId = localStorage.getItem('activeOrgId') || (user as any)?.orgId || null;

      set({
        user,
        token: getAccessToken(),
        isAuthenticated: true,
        isLoading: false,
        activeOrgId,
      });
      wsManager.connect();
      // Fetch orgs in background after auth is confirmed
      get().fetchOrganizations();
    } catch {
      clearAuthStorage();
      set({
        user: null,
        organization: null,
        subscription: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        organizations: [],
        activeOrgId: null,
        defaultOrgId: null,
      });
    }
  },

  switchOrg: async (orgId: string) => {
    const response = await fetch(`${API_BASE_URL}/organizations/${orgId}/switch`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        (errorData as { message?: string }).message || `Switch org failed (${response.status})`,
      );
    }

    const result = await response.json();
    if (!result.data) {
      throw new Error(result.message || 'Switch organization failed');
    }
    const { token, refreshToken: newRefreshToken, user, expires } = result.data;

    setAccessToken(token);
    if (newRefreshToken) setRefreshToken(newRefreshToken);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('tokenExpiry', expires);
    localStorage.setItem('activeOrgId', orgId);

    // Derive the active org object from the memberships list
    const activeOrg = get().organizations.find((m) => m.orgId === orgId);

    set({
      user,
      token,
      activeOrgId: orgId,
      organization: activeOrg?.organization || null,
    });

    // Reconnect WebSocket with new JWT
    wsManager.reconnect();

    // Refresh org memberships to pick up any server-side changes (e.g. defaultOrgId)
    get().fetchOrganizations();
  },

  fetchOrganizations: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/organizations/my-memberships`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });

      if (!response.ok) return;

      const memberships = await response.json();
      const defaultMembership = memberships.find((m: { isDefault: boolean }) => m.isDefault);
      const currentActiveOrgId = get().activeOrgId;

      // Validate activeOrgId exists in memberships; fall back to default or first org
      const validActiveOrg = memberships.find(
        (m: { orgId: string }) => m.orgId === currentActiveOrgId,
      );
      const resolvedActiveOrgId = validActiveOrg
        ? currentActiveOrgId
        : defaultMembership?.orgId || memberships[0]?.orgId || null;

      set({
        organizations: memberships,
        defaultOrgId: defaultMembership?.orgId || null,
        activeOrgId: resolvedActiveOrgId,
      });

      // Persist to localStorage
      if (defaultMembership?.orgId) {
        localStorage.setItem('defaultOrgId', defaultMembership.orgId);
      }
      if (resolvedActiveOrgId) {
        localStorage.setItem('activeOrgId', resolvedActiveOrgId);
      }
    } catch {
      // Non-critical — org data fetch failure doesn't break auth
    }
  },
}));
